import {
  __name,
  deepEqual,
  formatMilliseconds,
  getTimestamp,
  useLevelFilter,
  useNamespaceFilter
} from "./chunk-FLKB7XIS.js";

// src/browser/log-colors.ts
var colors = [
  "#0000CC",
  "#0000FF",
  "#0033CC",
  "#0033FF",
  "#0066CC",
  "#0066FF",
  "#0099CC",
  "#0099FF",
  "#00CC00",
  "#00CC33",
  "#00CC66",
  "#00CC99",
  "#00CCCC",
  "#00CCFF",
  "#3300CC",
  "#3300FF",
  "#3333CC",
  "#3333FF",
  "#3366CC",
  "#3366FF",
  "#3399CC",
  "#3399FF",
  "#33CC00",
  "#33CC33",
  "#33CC66",
  "#33CC99",
  "#33CCCC",
  "#33CCFF",
  "#6600CC",
  "#6600FF",
  "#6633CC",
  "#6633FF",
  "#66CC00",
  "#66CC33",
  "#9900CC",
  "#9900FF",
  "#9933CC",
  "#9933FF",
  "#99CC00",
  "#99CC33",
  "#CC0000",
  "#CC0033",
  "#CC0066",
  "#CC0099",
  "#CC00CC",
  "#CC00FF",
  "#CC3300",
  "#CC3333",
  "#CC3366",
  "#CC3399",
  "#CC33CC",
  "#CC33FF",
  "#CC6600",
  "#CC6633",
  "#CC9900",
  "#CC9933",
  "#CCCC00",
  "#CCCC33",
  "#FF0000",
  "#FF0033",
  "#FF0066",
  "#FF0099",
  "#FF00CC",
  "#FF00FF",
  "#FF3300",
  "#FF3333",
  "#FF3366",
  "#FF3399",
  "#FF33CC",
  "#FF33FF",
  "#FF6600",
  "#FF6633",
  "#FF9900",
  "#FF9933",
  "#FFCC00",
  "#FFCC33"
];
function supportsColors() {
  if (typeof window !== "undefined" && window.process && (window.process.type === "renderer" || window.process.__nwjs)) {
    return true;
  }
  if (typeof navigator !== "undefined" && navigator.userAgent && navigator.userAgent.toLowerCase().match(/(edge|trident)\/(\d+)/)) {
    return false;
  }
  return typeof document !== "undefined" && document.documentElement && document.documentElement.style && document.documentElement.style.WebkitAppearance || typeof window !== "undefined" && window.console && (window.console.firebug || window.console.exception && window.console.table) || typeof navigator !== "undefined" && navigator.userAgent && navigator.userAgent.toLowerCase().match(/firefox\/(\d+)/) && parseInt(RegExp.$1, 10) >= 31 || typeof navigator !== "undefined" && navigator.userAgent && navigator.userAgent.toLowerCase().match(/applewebkit\/(\d+)/);
}
__name(supportsColors, "supportsColors");
function selectColor(namespace) {
  let hash = 0;
  for (let i = 0; i < namespace.length; i++) {
    hash = (hash << 5) - hash + namespace.charCodeAt(i);
    hash |= 0;
  }
  return colors[Math.abs(hash) % colors.length];
}
__name(selectColor, "selectColor");

// src/browser/log-browser.ts
var styleFont = `font-family: "JetBrains Mono", Menlo; font-size: 11px;`;
var styleDefault = `${styleFont}`;
var styleBold = `font-weight: 600; ${styleFont}`;
var useColors = supportsColors();
var namespaces = {};
var time = getTimestamp();
function LoggerBrowserHandler(opt = {}) {
  const {
    filter = void 0,
    level = void 0,
    colors: colors2 = true,
    levelHelper = false,
    nameBrackets = true,
    padding = 16
  } = opt;
  const matchesNamespace = useNamespaceFilter(filter);
  const matchesLevel = useLevelFilter(level);
  return (msg) => {
    var _a, _b;
    if (!matchesLevel(msg.level))
      return;
    if (!matchesNamespace(msg.name))
      return;
    const timeNow = getTimestamp();
    let name = msg.name || "";
    let ninfo = namespaces[name || ""];
    if (ninfo == null) {
      ninfo = {
        color: selectColor(name)
      };
      namespaces[name] = ninfo;
    }
    const diff = formatMilliseconds(timeNow - time);
    let args;
    if (padding > 0) {
      name = name.padEnd(16, " ");
    }
    if (colors2 && useColors) {
      args = [`%c${name}%c 	%s %c+${diff}`];
      args.push(`color:${ninfo.color}; ${styleBold}`);
      args.push(styleDefault);
      args.push((_b = (_a = msg.messages) == null ? void 0 : _a[0]) != null ? _b : "");
      args.push(`color:${ninfo.color};`);
      args.push(...msg.messages.slice(1));
    } else {
      args = [name, ...msg.messages, `+${diff}`];
    }
    switch (msg.level) {
      case 1 /* info */:
        if (opt.levelHelper)
          args[0] = `I|*   ` + args[0];
        console.info(...args);
        break;
      case 2 /* warn */:
        if (opt.levelHelper)
          args[0] = `W|**  ` + args[0];
        console.warn(...args);
        break;
      case 3 /* error */:
        if (opt.levelHelper)
          args[0] = `E|*** ` + args[0];
        console.error(...args);
        break;
      default:
        if (opt.levelHelper)
          args[0] = `D|    ` + args[0];
        console.debug(...args);
        break;
    }
  };
}
__name(LoggerBrowserHandler, "LoggerBrowserHandler");
function LoggerBrowserSetupDebugFactory(opt = {}) {
  var _a, _b;
  const filter = (_b = (_a = opt.filter) != null ? _a : localStorage.zeed) != null ? _b : localStorage.debug;
  return /* @__PURE__ */ __name(function LoggerBrowserDebugFactory(name = "") {
    let log;
    const matches = useNamespaceFilter(filter);
    if (matches(name)) {
      let fixedArgs = [];
      if (useColors) {
        const color = selectColor(name);
        fixedArgs.push(`%c${name.padEnd(16, " ")}%c 	%s`);
        fixedArgs.push(`color:${color}; ${styleBold}`);
        fixedArgs.push(styleDefault);
      } else {
        fixedArgs.push(`[${name}] 	%s`);
      }
      log = console.debug.bind(console, ...fixedArgs);
      log.debug = console.debug.bind(console, ...fixedArgs);
      log.info = console.info.bind(console, ...fixedArgs);
      log.warn = console.warn.bind(console, ...fixedArgs);
      log.error = console.error.bind(console, ...fixedArgs);
      log.assert = console.assert.bind(console);
      log.assertEqual = function(value, expected, ...args) {
        let equal = deepEqual(value, expected);
        if (!equal) {
          log.assert(equal, `Assert did fail. Expected ${expected} got ${value}`, expected, value, ...args);
        }
      };
      log.assertNotEqual = function(value, expected, ...args) {
        let equal = deepEqual(value, expected);
        if (equal) {
          log.assert(equal, `Assert did fail. Expected ${expected} not to be equal with ${value}`, expected, value, ...args);
        }
      };
    } else {
      const noop = /* @__PURE__ */ __name(() => {
      }, "noop");
      log = noop;
      log.debug = noop;
      log.info = noop;
      log.warn = noop;
      log.error = noop;
      log.assert = noop;
      log.assertEqual = noop;
      log.assertNotEqual = noop;
    }
    log.extend = (subName) => {
      return LoggerBrowserDebugFactory(name ? `${name}:${subName}` : subName);
    };
    return log;
  }, "LoggerBrowserDebugFactory");
}
__name(LoggerBrowserSetupDebugFactory, "LoggerBrowserSetupDebugFactory");
function activateConsoleDebug(opt = {}) {
  console.info("activateConsoleDebug is activated by default in browsers");
}
__name(activateConsoleDebug, "activateConsoleDebug");

export {
  supportsColors,
  selectColor,
  LoggerBrowserHandler,
  LoggerBrowserSetupDebugFactory,
  activateConsoleDebug
};
//# sourceMappingURL=chunk-NZQXKE2T.js.map